import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Image, Upload, Trash2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function EventGalleryManager({ eventId, eventTitle, open, onOpenChange }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (open && eventId) {
      fetchGallery();
    }
  }, [open, eventId]);

  const fetchGallery = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/events/${eventId}/gallery`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      // If 404, gallery is empty
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecciona una imagen');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      await axios.post(
        `${API}/admin/events/${eventId}/gallery`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Foto añadida a la galería');
      setSelectedFile(null);
      fetchGallery();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoIndex) => {
    if (!window.confirm('¿Eliminar esta foto de la galería?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/admin/events/${eventId}/gallery/${photoIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Foto eliminada');
      fetchGallery();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Error al eliminar la foto');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-purple-600" />
            Galería del Evento
          </DialogTitle>
          <DialogDescription>
            {eventTitle} - Gestiona las fotos oficiales del evento
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Upload Section */}
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Añadir Foto a la Galería
            </h4>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="cursor-pointer"
                />
              </div>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Subir
                  </>
                )}
              </Button>
            </div>
            <p className="text-sm text-purple-600 mt-2">
              Las fotos de la galería son visibles para los usuarios inscritos en el evento.
            </p>
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-gray-600">Cargando galería...</p>
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
              <Image className="w-16 h-16 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-lg">La galería está vacía</p>
              <p className="text-sm text-gray-400 mt-1">
                Sube fotos del evento para compartir con los participantes
              </p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-3">{photos.length} fotos en la galería</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {photos.map((photoUrl, index) => (
                  <div key={photoUrl} className="relative group aspect-square">
                    <img
                      src={photoUrl.startsWith('http') ? photoUrl : `${API.replace('/api', '')}${photoUrl}`}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border"
                      onClick={() => window.open(photoUrl.startsWith('http') ? photoUrl : `${API.replace('/api', '')}${photoUrl}`, '_blank')}
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2 h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDelete(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      #{index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
